import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut, apiPutOnExit, ApiError } from "../api";
import type { MembershipRole, PersonPayload } from "./memberships";
import type { ID } from "../id";

/** One draft row on the wire — see membership.SeasonRegistrationEntry. */
export interface SeasonRegistrationEntry {
  row_key: string;
  membership_id?: ID;
  person_id?: ID;
  /** Omit to leave the stored Person untouched. */
  person?: PersonPayload;
  /**
   * The person as the draft loaded them. With it the server writes only the
   * fields that differ from it and refuses one changed on both sides
   * (`code: "conflict"`), instead of overwriting what changed meanwhile.
   */
  person_base?: PersonPayload;
  roles?: MembershipRole[];
  /** The roles as the draft loaded them: the server merges the difference. */
  roles_base?: MembershipRole[];
  remove?: boolean;
}

export interface SeasonRegistrationPayload {
  from_season_id?: ID;
  entries: SeasonRegistrationEntry[];
  /** Season documents ticked on the page (seasondoc). */
  agreed_document_version_ids?: ID[];
  /** The rozpracovaná registrace this submit was built from; the server deletes it with the write. */
  draft_revision?: number;
}

export interface SeasonRegistrationResult {
  season_id: ID;
  created: number;
  updated: number;
  removed: number;
  person_ids: ID[];
  cmf_ids_allocated: number;
}

/** A 422 entry from the batch endpoint, bound to the row that produced it. */
export interface RowFieldError {
  row_key: string;
  person_id: ID | null;
  field: string;
  message: string;
  /** "conflict": the field changed on the server too since the draft loaded it. */
  code?: string;
  /** With "conflict": the value stored now, as the API sends it. */
  theirs?: string;
}

/**
 * parseRowErrors groups a rejected submit's errors by row so each message can
 * be shown on the cell that caused it. Entries whose row is no longer in the
 * draft fall back to the page-level message rather than vanishing.
 */
export function parseRowErrors(
  err: unknown,
  knownKeys: Set<string>,
): {
  byRow: Map<string, Record<string, string>>;
  message: string | null;
  /** Per row, each field changed on both sides with the value stored now. */
  conflicts: Map<string, Record<string, string>>;
} {
  const body = (err as ApiError | undefined)?.body as
    | { message?: string; errors?: RowFieldError[] }
    | undefined;
  const byRow = new Map<string, Record<string, string>>();
  const orphaned: string[] = [];
  const conflicts = new Map<string, Record<string, string>>();

  for (const e of body?.errors ?? []) {
    if (!e || typeof e.field !== "string") continue;
    if (!e.row_key || !knownKeys.has(e.row_key)) {
      orphaned.push(e.message);
      continue;
    }
    if (e.code === "conflict" && typeof e.theirs === "string" && e.field.startsWith("person.")) {
      const c = conflicts.get(e.row_key) ?? {};
      c[e.field.slice("person.".length)] = e.theirs;
      conflicts.set(e.row_key, c);
      continue;
    }
    const current = byRow.get(e.row_key) ?? {};
    // The server sends person.* paths for person fields and bare names for
    // the rest, matching DraftFieldPath.
    if (!(e.field in current)) current[e.field] = e.message;
    byRow.set(e.row_key, current);
  }

  const message =
    orphaned.length > 0
      ? [body?.message, ...orphaned].filter(Boolean).join(" ")
      : (body?.message ?? null);
  return { byRow, message, conflicts };
}

export function useSubmitSeasonRegistration(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<SeasonRegistrationResult, ApiError, SeasonRegistrationPayload>({
    mutationFn: (payload) =>
      apiPost<SeasonRegistrationResult>(`/teams/${teamId}/season-registration`, payload),
    // The page binds every error to its row, so a toast would only repeat it.
    meta: { suppressErrorToast: true },
    // A refusal is often the roster having moved under the draft (a closed
    // row, a removed person). Refetching is what lets the page notice and
    // offer to merge; the draft itself is not touched by it.
    onError: () => qc.invalidateQueries({ queryKey: ["teams", teamId, "members"] }),
    // Returned, not fired and forgotten: the page resets its draft the moment
    // mutateAsync resolves, and it must resolve against the roster the submit
    // just wrote rather than the stale one still in cache.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["teams", teamId, "members"] }),
        qc.invalidateQueries({ queryKey: ["teams", teamId] }),
        qc.invalidateQueries({ queryKey: ["teams"] }),
        // ["me","teams"] is a disjoint prefix from ["teams"], so the line
        // above does not reach it — and useMyTeams is what the dashboard and
        // the sidebar read. Without this both kept the club's old state for
        // up to the 30s staleTime: after registering, still no MČR entry
        // offered; after unregistering, still offering one.
        qc.invalidateQueries({ queryKey: ["me", "teams"] }),
        // …and the dashboard's alerts, which the server computes from the
        // same registered-membership count (service/dashboard: roster_empty).
        // They are a separate query, so „useMyTeams is what the dashboard
        // reads" was only half of it.
        qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] }),
        qc.invalidateQueries({ queryKey: ["persons"] }),
      ]),
  });
}

export interface UnregisterSeasonResult {
  season_id: ID;
  demoted: number;
  person_ids: ID[];
}

/**
 * useUnregisterTeamSeason takes the club back out of the active season: every
 * registered membership becomes a nečlen and the club reads „Jen pohárovky"
 * again.
 *
 * The mirror of useSubmitSeasonRegistration, and open exactly as long: the
 * server refuses it once the registration window shuts, for everybody
 * including a Správce členské základny (ErrUnregisterWindowClosed), because
 * the členský příspěvek is settled from that moment on.
 */
export function useUnregisterTeamSeason(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<UnregisterSeasonResult, ApiError, void>({
    mutationFn: () => apiDelete<UnregisterSeasonResult>(`/teams/${teamId}/season-registration`),
    // The confirm dialog renders the refusal beside the button that raised
    // it, so a toast would say the same thing twice.
    meta: { suppressErrorToast: true },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["teams", teamId, "members"] }),
        qc.invalidateQueries({ queryKey: ["teams", teamId] }),
        qc.invalidateQueries({ queryKey: ["teams"] }),
        // ["me","teams"] is a disjoint prefix from ["teams"], so the line
        // above does not reach it — and useMyTeams is what the dashboard and
        // the sidebar read. Without this both kept the club's old state for
        // up to the 30s staleTime: after registering, still no MČR entry
        // offered; after unregistering, still offering one.
        qc.invalidateQueries({ queryKey: ["me", "teams"] }),
        // …and the dashboard's alerts, which the server computes from the
        // same registered-membership count (service/dashboard: roster_empty).
        // They are a separate query, so „useMyTeams is what the dashboard
        // reads" was only half of it.
        qc.invalidateQueries({ queryKey: ["dashboard", "alerts"] }),
        qc.invalidateQueries({ queryKey: ["persons"] }),
      ]),
  });
}

/**
 * The rozpracovaná registrace (docs/rozpracovana-registrace.md): the
 * workspace saved part-way. `payload` is the page's own format
 * (SeasonRegistrationPage/draftPersist.ts), opaque here.
 */
export interface SeasonRegistrationDraft {
  payload: unknown;
  revision: number;
  updated_ms: number;
  updated_by_name: string;
}

export interface SeasonRegistrationDraftState {
  draft: SeasonRegistrationDraft | null;
  /** Only the team's leader writes it, and only while the window is open. */
  can_write: boolean;
}

const draftKey = (teamId: ID) => ["teams", teamId, "season-registration-draft"];

export function useSeasonRegistrationDraft(teamId: ID) {
  return useQuery<SeasonRegistrationDraftState>({
    queryKey: draftKey(teamId),
    queryFn: () => apiGet<SeasonRegistrationDraftState>(`/teams/${teamId}/season-registration/draft`),
    // Fetched afresh every time the page opens (the page waits for it,
    // isFetchedAfterMount): a cached copy from a visit minutes ago would
    // offer an old revision, and the first save would conflict with the
    // leader's own. Within one visit the page tracks the revision itself and
    // asks only once, so a focus refetch would only cost a request.
    staleTime: Infinity,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  });
}

/** The 409 a save gets when it was built on a revision no longer stored. */
export interface DraftChangedBody {
  code: "draft_changed";
  message: string;
  /** 0: there is no draft any more (submitted or discarded elsewhere). */
  revision: number;
  updated_by_name: string;
  updated_ms: number;
}

export function draftChangedBody(err: unknown): DraftChangedBody | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as Partial<DraftChangedBody> | null;
  return body?.code === "draft_changed" ? (body as DraftChangedBody) : null;
}

export function useSaveSeasonRegistrationDraft(teamId: ID) {
  return useMutation<SeasonRegistrationDraft, ApiError, { revision: number; payload: unknown }>({
    mutationFn: (body) => apiPut<SeasonRegistrationDraft>(`/teams/${teamId}/season-registration/draft`, body),
    // An autosave: the page says how it went in the draft bar.
    meta: { suppressErrorToast: true },
  });
}

/**
 * Deletes the draft. With a revision (the autosave clearing a draft that went
 * empty) the server refuses with `draft_changed` if another save came first;
 * without one („Začít znovu") it deletes whatever is stored.
 */
export function useDiscardSeasonRegistrationDraft(teamId: ID) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { revision?: number } | void>({
    mutationFn: (v) =>
      apiDelete<void>(
        `/teams/${teamId}/season-registration/draft${v && v.revision ? `?revision=${v.revision}` : ""}`,
      ),
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.setQueryData<SeasonRegistrationDraftState>(draftKey(teamId), (old) =>
      old ? { ...old, draft: null } : old,
    ),
  });
}

/** The last save as the page is closed; best effort, the debounced save normally got there first. */
export function saveSeasonRegistrationDraftOnExit(teamId: ID, body: { revision: number; payload: unknown }): void {
  apiPutOnExit(`/teams/${teamId}/season-registration/draft`, body);
}
